import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { SidebarModule } from 'primeng/sidebar';
import { InputTextModule } from 'primeng/inputtext';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { injectStream } from '@langchain/angular';
import { QueryKeys } from '@portal/app/shared/services/queries/query-keys';
import {
  MeasuredBffTransport,
  type MeasuredBffStreamContext
} from '@portal/app/shared/services/queries/agent-stream-transport';
import {
  TabViewHeaderSize,
  type ITabPanelHeader
} from '@design-system/components/m-tab-view/m-tab-view.component.types';
import { DesignSystemModule } from '@design-system/design-system.module';
import { environment } from '@portal/environments/environment';
import { getAccessToken } from '@portal/app/shared/helpers/helpers';
import { AiPanelService } from '@portal/app/shared/services/ai-panel.service';
import { MiaContextService } from '@portal/app/shared/services/mia-context.service';
import { AiChatService } from '@portal/app/shared/components/ai-assistant-panel/ai-chat.service';
import { PortalChatThreadComponent } from '@portal/app/shared/components/ai-assistant-panel/thread/chat-thread.component';
import { PortalPromptsViewComponent } from '@portal/app/shared/components/ai-assistant-panel/state/prompts-view.component';
import { AgentsService } from '@libs/api';
import type {
  PanelAgentDTO as PanelAgent,
  ConversationStarterDTO as ConversationStarter
} from '@libs/api';

@Component({
  selector: 'portal-ai-assistant-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AiChatService],
  imports: [
    SidebarModule,
    InputTextModule,
    FormsModule,
    DesignSystemModule,
    PortalChatThreadComponent,
    PortalPromptsViewComponent
  ],
  templateUrl: './ai-assistant-panel.component.html',
  styleUrl: './ai-assistant-panel.component.scss'
})
export class AiAssistantPanelComponent {
  private readonly agentsService = inject(AgentsService);
  readonly aiPanelService = inject(AiPanelService);
  private readonly miaContext = inject(MiaContextService);
  readonly chat = inject(AiChatService);

  /** Stamped at sendMessage()/askConversationStarter() so the transport's
   * resolveContext picks it up at fetch time. One stream controller for
   * the panel's lifetime regardless of which agent the user invokes. */
  private readonly _activeAssistantId = signal<string>('');

  private readonly transport = new MeasuredBffTransport({
    apiUrl: environment.apiDomain ?? '',
    ssoProvider: environment.ssoProvider,
    resolveAuthToken: () => this.resolveAuthToken(),
    resolveContext: (): MeasuredBffStreamContext => ({
      clientId: this.miaContext.clientId(),
      brandId: this.miaContext.brandId(),
      assistantId: this._activeAssistantId()
    }),
    onRunMetadata: ({ run_id }) => this.chat.handleRunMetadata(run_id)
  });

  readonly stream = injectStream({ transport: this.transport });

  readonly userDisplayName: string = this.miaContext.userName();

  constructor(@Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth) {
    this.chat.bind(
      this.stream as unknown as Parameters<typeof this.chat.bind>[0]
    );

    let lastError: unknown = null;
    effect(() => {
      const err = this.stream.error();
      if (err && err !== lastError) {
        lastError = err;
        untracked(() => this.chat.handleError(err));
      } else if (!err) {
        lastError = null;
      }
    });

    // Reset transient panel state on page nav so a prior page's agent
    // selection doesn't bleed into the new page's panel-config.
    effect(() => {
      this.miaContext.dashboardLiteralId();
      untracked(() => {
        this._activeAssistantId.set('');
        this.activeStarterTab.set('my');
      });
    });
  }

  readonly panelConfigQuery = injectQuery(() => {
    const clientId = this.miaContext.clientId();
    const brandId = this.miaContext.brandId();
    const dashboardLiteralId = this.miaContext.dashboardLiteralId();
    const enabled = this.miaContext.isReady() && this.aiPanelService.visible();
    return {
      queryKey: [
        QueryKeys.PanelConfig,
        clientId,
        brandId,
        dashboardLiteralId
      ] as const,
      queryFn: () =>
        firstValueFrom(
          this.agentsService.getPanelConfig(
            clientId,
            brandId,
            dashboardLiteralId || undefined
          )
        ),
      enabled,
      staleTime: 5 * 60 * 1000
    };
  });

  readonly panelAgents = computed(
    () => this.panelConfigQuery.data()?.agents ?? []
  );

  readonly conversationStarters = computed(
    () => this.panelConfigQuery.data()?.conversationStarters ?? []
  );

  /** Synthesized chips for agents that ship without conversation_starters metadata. */
  readonly derivedStarters = computed<ConversationStarter[]>(() => {
    const coveredAssistantIds = new Set(
      this.conversationStarters()
        .map((s) => s.assistantId)
        .filter((id): id is string => !!id)
    );
    return this.panelAgents()
      .filter((a) => !coveredAssistantIds.has(a.assistantId))
      .map((a) => {
        const desc = a.description?.trim();
        return {
          label: desc || `Ask ${a.agentName}`,
          promptTemplate:
            desc || `Hi ${a.agentName}, what can you help me with?`,
          assistantId: a.assistantId,
          dashboardLiteralIds: a.dashboardLiteralIds ?? [],
          visibility: a.visibility ?? 'shared'
        };
      });
  });

  readonly myStarters = computed(() => [
    ...this.conversationStarters().filter((s) => s.visibility === 'private'),
    ...this.derivedStarters().filter((s) => s.visibility === 'private')
  ]);

  readonly sharedStarters = computed(() => [
    ...this.conversationStarters().filter((s) => s.visibility === 'shared'),
    ...this.derivedStarters().filter((s) => s.visibility === 'shared')
  ]);

  /** Agent name for the sub-header: active starter > page-scoped agent > 'Mia'. */
  readonly activeAgentName = computed(() => {
    const activeId = this._activeAssistantId();
    if (activeId) {
      const byId = this.panelAgents().find((a) => a.assistantId === activeId);
      if (byId) return byId.agentName;
    }
    const literalId = this.miaContext.dashboardLiteralId();
    const byPage = literalId
      ? this.panelAgents().find((a) =>
          (a.dashboardLiteralIds ?? []).includes(literalId)
        )
      : null;
    return byPage?.agentName ?? 'Mia';
  });

  readonly activeStarterTab = signal<'my' | 'shared'>('my');

  /** True once any agent has been invoked. `_activeAssistantId` covers the
   * clear → submit transition window so the welcome screen doesn't flash. */
  readonly hasInvokedAgent = computed(
    () => this.chat.messages().length > 0 || !!this._activeAssistantId()
  );

  readonly isAgentResponding = computed(() => this.stream.isLoading());

  readonly starterTabHeaderSize = TabViewHeaderSize.small;

  readonly starterTabHeaders = computed<ITabPanelHeader[]>(() => [
    { header: 'My Prompts', disabled: false, showCount: false, count: 0 },
    { header: 'Shared Prompts', disabled: false, showCount: false, count: 0 }
  ]);

  onStarterTabChange(index: number): void {
    this.activeStarterTab.set(index === 0 ? 'my' : 'shared');
  }

  readonly chatInputValue = signal('');

  @ViewChild('chatInput') private chatInputRef?: ElementRef<HTMLInputElement>;

  readonly mentionDropdownVisible = signal(false);
  readonly mentionQuery = signal('');

  readonly filteredMentionAgents = computed(() => {
    const q = this.mentionQuery().toLowerCase();
    if (!q) return this.panelAgents();
    return this.panelAgents().filter((a) =>
      a.agentName.toLowerCase().includes(q)
    );
  });

  resetThread(): void {
    this.chat.clear();
    this._activeAssistantId.set('');
  }

  askConversationStarter(starter: ConversationStarter): void {
    if (!starter.assistantId) {
      this.chat.handleError(
        new Error('Conversation starter is missing its agent.')
      );
      return;
    }

    // Stamp active agent BEFORE clearing so hasInvokedAgent stays truthy
    // across the clear → submit transition.
    this._activeAssistantId.set(starter.assistantId);
    this.chat.clear();

    void this.stream.submit({
      messages: [{ type: 'human', content: starter.promptTemplate }]
    });
  }

  onChatInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.chatInputValue.set(value);
    const atIdx = value.lastIndexOf('@');
    if (atIdx >= 0) {
      const query = value.slice(atIdx + 1);
      if (!query.includes(' ')) {
        this.mentionQuery.set(query);
        this.mentionDropdownVisible.set(true);
        return;
      }
    }
    this.mentionDropdownVisible.set(false);
    this.mentionQuery.set('');
  }

  selectMentionAgent(agent: PanelAgent): void {
    const atIdx = this.chatInputValue().lastIndexOf('@');
    if (atIdx >= 0) {
      this.chatInputValue.set(
        this.chatInputValue().substring(0, atIdx) + `@${agent.agentName} `
      );
    }
    this.mentionDropdownVisible.set(false);
    this.mentionQuery.set('');
  }

  readonly isFullscreen = signal(false);

  toggleFullscreen(): void {
    this.isFullscreen.update((v) => !v);
  }

  focusChatInput(): void {
    this.chatInputRef?.nativeElement?.focus();
  }

  sendMessage(): void {
    const question = this.chatInputValue().trim();
    if (!question || this.isAgentResponding()) return;

    this.chatInputValue.set('');
    this.mentionDropdownVisible.set(false);

    const dashboardLiteralId = this.miaContext.dashboardLiteralId();

    const mentionedAgent = this.panelAgents().find((a) =>
      question.includes(`@${a.agentName}`)
    );

    // Pin follow-ups to the open conversation's agent; the chain below re-resolves
    // per send and would otherwise re-route mid-thread. An @-mention still overrides.
    const activeAssistantId = this._activeAssistantId();
    const hasOpenConversation =
      !!activeAssistantId && this.chat.messages().length > 0;

    const assistantId =
      mentionedAgent?.assistantId ??
      (hasOpenConversation ? activeAssistantId : undefined) ??
      this.panelAgents().find((a) =>
        (a.dashboardLiteralIds ?? []).includes(dashboardLiteralId)
      )?.assistantId ??
      this.panelAgents()[0]?.assistantId ??
      '';

    if (!assistantId) {
      this.chat.handleError(new Error('No agent available for this page.'));
      return;
    }

    this._activeAssistantId.set(assistantId);

    void this.stream.submit({
      messages: [{ type: 'human', content: question }]
    });
  }

  stopStream(): void {
    void this.stream.stop();
  }

  /**
   * Resolve the bearer token for the streaming fetch. Mirrors
   * AuthenticationInterceptor: prefer environment.authToken when running
   * locally against a localhost API in non-prod; otherwise use the live
   * Okta access token (aud=api://default, required for RFC 8693 token exchange in measured-api).
   */
  private async resolveAuthToken(): Promise<string> {
    const oktaToken = await getAccessToken(this.oktaAuth);
    const useDevToken =
      !environment.production &&
      (!environment.innovation ||
        (environment.innovation &&
          environment.apiDomain?.includes('localhost'))) &&
      environment.apiDomain?.includes('localhost') &&
      !!environment.authToken;
    return (useDevToken ? environment.authToken : oktaToken) ?? '';
  }
}
